
import DialogTemplate from '@/_components/dialog/template/template.vue'
import { deleteRequest, getRequest, patchRequest, postRequest } from '@/plugins/api-axios'
import CompanyTreeX from '@/views/Project/ch/components/companyTreeX.vue'
import Contractors from '@/views/Project/ch/components/contractors.vue'
import { getURL } from '@/utils/axios'
import ProjectMixin from '@/_mixins/project'
import { deserialise, serialise } from 'kitsu-core'
import { debounce } from 'lodash'

export default {
    name: 'ContractorRolesDialog',
    mixins: [ProjectMixin],
    components: {
        Contractors,
        CompanyTreeX,
        DialogTemplate,
    },
    props: {
        props: Object,
    },
    provide: {
        hierarchyProvider: {},
    },
    data() {
        return {
            tree: [] as any[],
            contractors: [] as any[],
            roleAssignments: this.props.roles,
            contractorAssignments: [] as any[],
            isLoading: false,
            hasChanged: false,
            searchTerm: '',
            showContractorsWithAssignment: false,
            worker: null as Worker | null,
        }
    },
    computed: {
        contractor() {
            return this.contractors[0]
        },
        hasManageAccess() {
            return this.hasPermission(this.allPermissions, 'authorization.manage_access')
        },
        hasDelegateAccess() {
            return this.hasPermission(this.allPermissions, 'authorization.delegate_access')
        },
        contractorAssignmentIds() {
            const ids = {}
            this.contractorAssignments.forEach((assignment) => {
                ids[assignment.contractor.data.id] = true
            })
            return ids
        },
        selectedContractorAssignment(): any {
            if (!this.contractor) {
                return null
            }

            return this.contractorAssignments.find(
                (assignment: any) => assignment.contractor.data.id === this.contractor.id
            )
        },
        userId() {
            // user might come from ProjectUserModel which uses _id
            return this.props.user._user?._id || this.props.user.user?.data?.id
        },
        projectUserId() {
            // user might come from ProjectUserModel which uses _id
            return this.props.user._id || this.props.user.id
        },
        userAssignedRoles() {
            const roles = [...(this.currentProjectUser?._roles || [])]

            if (Array.isArray(this.currentProjectUser?.contractorAssignments)) {
                let contractorAssignment = this.currentProjectUser.contractorAssignments.find(
                    (assignment) => assignment.contractor._id === this.contractor.id
                )
                contractorAssignment?.roleAssignments.forEach((ra) => {
                    roles.push({ ...ra.role })
                })
            }

            return roles
        },
    },
    watch: {
        contractor() {
            this.updateRoleAssignments()
        },
        showContractorsWithAssignment() {
            this.filterContractors()
        },
    },
    methods: {
        isRoleDisabled(role: any) {
            if (role.disabled) {
                return true
            } else if (this.hasManageAccess) {
                return false
            } else if (this.hasDelegateAccess) {
                return !this.userAssignedRoles.find((r) => r._id === role._id)
            }
            return true
        },
        handleSearchTermChanged: debounce(function () {
            this.filterContractors()
        }, 500),
        updateRoleAssignments() {
            const roleAssignments = [] as any[]
            this.roleAssignments = []

            this.selectedContractorAssignment?.roleAssignments.data.forEach((ra) => {
                roleAssignments.push({
                    ...ra.role.data,
                    _id: ra.role.data.id,
                    roleAssignment: ra,
                    disabled: false, // disable either when role is assigning or *Apply to all subcontractor* is changing (prevent race condition with POST/PATCH),
                })
            })

            this.props.roles.forEach((role) => {
                if (roleAssignments.find((ra) => ra._id === role._id)) return

                roleAssignments.push({
                    ...role,
                    roleAssignment: {},
                    disabled: false,
                })
            })

            roleAssignments
                .sort((a, b) => a.name?.localeCompare(b.name))
                .forEach((item) => {
                    if (!item.name) {
                        this.roleAssignments.unshift(item)
                    } else {
                        this.roleAssignments.push(item)
                    }
                })
        },
        async createContractorAssignment() {
            if (!this.contractor || !this.userId) {
                return
            }

            const contractorAssignmentPayload = {
                projectUser: {
                    data: { type: 'projectUser', id: this.projectUserId },
                },
                contractor: {
                    data: { type: 'contractor', id: this.contractor.id },
                },
            }
            const contractorAssignmentDataResponse = await postRequest(
                getURL({
                    path: 'projectUserContractorAssignments',
                    base: this.url_authorization,
                    params: {
                        include: 'roleAssignments,contractor',
                    },
                }),
                {},
                serialise('projectUserContractorAssignment', contractorAssignmentPayload).data
            )
            const contractorAssignment = deserialise(contractorAssignmentDataResponse.data).data
            this.contractorAssignments.push(contractorAssignment)
        },
        async removeContractorAssignment() {
            if (!this.selectedContractorAssignment) {
                return
            }

            await deleteRequest(
                getURL({
                    path: `projectUserContractorAssignments/${this.selectedContractorAssignment.id}`,
                    base: this.url_authorization,
                }),
                {}
            )

            this.contractorAssignments = this.contractorAssignments.filter(
                (assignment) => assignment.id !== this.selectedContractorAssignment.id
            )
            this.contractors = []
            if (this.props.contractor) {
                this.$nextTick(() => (this.contractors = [this.props.contractor]))
            }
            // if contractor is removed, filter it out from contractor list
            this.filterContractors()
        },
        async changeIncludeSubcontractors(value: boolean, role: any) {
            if (!role.roleAssignment.id) {
                this.addOrUpdateContractorRoleAssignemnt(true, role)
            } else {
                patchRequest(
                    getURL({
                        path: `projectUserContractorRoleAssignments/${role.roleAssignment.id}`,
                        base: this.url_authorization,
                    }),
                    {},
                    serialise('projectUserContractorRoleAssignment', {
                        id: role.roleAssignment.id,
                        includeSubcontractors: value,
                    }).data
                )
            }
        },
        async removeContractorRoleAssignment(role: any) {
            if (!role.roleAssignment.id) {
                return
            }

            role.roleAssignment.includeSubcontractors = false // optimistic update
            await deleteRequest(
                getURL({
                    path: `projectUserContractorRoleAssignments/${role.roleAssignment.id}`,
                    base: this.url_authorization,
                }),
                {}
            )
            this.selectedContractorAssignment.roleAssignments.data =
                this.selectedContractorAssignment.roleAssignments.data.filter(
                    (ra: any) => ra.role.data.id !== role._id
                )
            this.updateRoleAssignments()
        },
        async addOrUpdateContractorRoleAssignemnt(adding: boolean, role: any) {
            if (!this.selectedContractorAssignment) {
                return
            }

            if (!adding) {
                return this.removeContractorRoleAssignment(role)
            }

            if (role.kind === 'system.contractorAdmin') {
                role.roleAssignment.includeSubcontractors = true
            }
            role.disabled = true
            const roleAssignmentResponse = await postRequest(
                getURL({
                    path: 'projectUserContractorRoleAssignments',
                    base: this.url_authorization,
                    params: { skipLinks: '', include: 'role' },
                }),
                {},
                serialise('projectUserContractorRoleAssignment', {
                    includeSubcontractors: role.roleAssignment.includeSubcontractors,
                    contractorAssignment: {
                        data: {
                            type: this.selectedContractorAssignment.type,
                            id: this.selectedContractorAssignment.id,
                        },
                    },
                    role: {
                        data: { type: role.type, id: role._id },
                    },
                }).data
            )
            this.selectedContractorAssignment.roleAssignments.data.push(
                deserialise(roleAssignmentResponse.data).data
            )
            this.updateRoleAssignments()
        },
        filterContractors() {
            // if contractor is selected and it has no assignment, clear it
            if (
                this.showContractorsWithAssignment &&
                this.contractor?.id &&
                !this.contractorAssignmentIds[this.contractor.id]
            ) {
                this.contractors = []
            }

            this.worker?.postMessage({
                type: 'filterContractor',
                payload: {
                    includeAssignment: this.showContractorsWithAssignment,
                    assignmentIds: this.contractorAssignmentIds,
                    searchTerm: this.searchTerm,
                    tree: this.tree,
                },
            })
        },
    },
    async created() {
        try {
            this.isLoading = true

            const [userResponse, hierarchyResponse] = await Promise.all([
                getRequest(
                    getURL({
                        path: 'projectUsers',
                        base: this.url_authorization,
                        params: {
                            'filter[user]': this.userId,
                            'filter[project]': this.currentProjectId,
                            include:
                                'contractorAssignments,roles,contractorAssignments.roleAssignments,contractorAssignments.roleAssignments.role,contractorAssignments.contractor,availableContractorsForAssignment',
                            skipLinks: '',
                        },
                    })
                ),
                this.props.contractor
                    ? Promise.resolve(null)
                    : getRequest(
                          getURL({
                              path: `contractorHierarchies/${this.currentProjectId}`,
                              base: this.contractorBaseUrl,
                              params: {
                                  include: 'nodes,nodes.parent,nodes.company,nodes.companyDetails',
                                  skipLinks: '',
                              },
                          })
                      ),
            ])

            const [user] = deserialise(userResponse.data).data

            this.contractorAssignments = user.contractorAssignments.data
            this.unwatchContractorAssignments = this.$watch(
                'contractorAssignments',
                () => (this.hasChanged = true)
            )

            // wait for user data before setting contractor
            if (this.props.contractor) {
                this.contractors = [this.props.contractor]
            }

            if (hierarchyResponse) {
                let hierarchyData = deserialise(hierarchyResponse.data)
                let ids = {}
                let companyDetails = {}
                hierarchyData.data.nodes.data.forEach((node) => {
                    ids[node.id] = { ...node, children: [] }
                    if (node.companyDetails?.data?.id) {
                        companyDetails[node.companyDetails.data.id] = node.id
                    }
                })
                hierarchyData.data.nodes.data.forEach((node) => {
                    const addingNode = ids[node.id]
                    addingNode._isMatch = !node.isArchived
                    if (!node.parent.data) {
                        if (!this.tree) this.tree = []
                        this.tree.push(addingNode)
                    } else {
                        ids[node.parent.data.id].children.push(addingNode)
                        ids[node.parent.data.id].children.sort((a, b) => a.index - b.index)
                    }
                })
            }
        } catch (e) {
            console.error(e)
        } finally {
            this.isLoading = false
        }
    },
    beforeDestroy() {
        this.unwatchContractorAssignments?.()
    },
}
